/**
 * Action Registry Implementation
 * Central registry for all entity actions with strict TypeScript
 */

import { logger } from "@/lib/logger";

import type { EntityKey, EntityRowType } from "../types/entity-keys";
import type { DetailActionStrict } from "../types/detail-action";
import type { IActionRegistry, EntityActionMapType } from "../types/registry";
import type { ActionContextType } from "../types/action-names";

/**
 * Central action registry
 * Stores and retrieves actions for all entities with full type safety
 */
class ActionRegistry implements IActionRegistry {
  private registry: Map<EntityKey, DetailActionStrict<any>[]> = new Map();

  /**
   * Get all actions for an entity
   */
  getActionsForEntity<K extends EntityKey>(
    entity: K
  ): DetailActionStrict<EntityRowType<K>>[] {
    const actions = this.registry.get(entity);
    if (!actions) {
      logger.warn("No actions registered for entity", { entity });
      return [];
    }
    return actions as DetailActionStrict<EntityRowType<K>>[];
  }

  /**
   * Get actions for an entity filtered by context
   * For detail/table surfaces, return the same unified list.
   * Bulk remains filtered by bulkable.
   */
  getActionsForEntityAndContext<K extends EntityKey>(
    entity: K,
    context: ActionContextType
  ): DetailActionStrict<EntityRowType<K>>[] {
    const allActions = this.getActionsForEntity(entity);

    return allActions.filter((action) => {
      // Keep bulk behavior explicit; other surfaces share one action list.
      if (context === "bulk" && !action.bulkable) {
        return false;
      }

      return true;
    });
  }

  /**
   * Register actions for an entity
   */
  registerActionsForEntity<K extends EntityKey>(
    entity: K,
    actions: DetailActionStrict<EntityRowType<K>>[]
  ): void {
    if (this.registry.has(entity)) {
      logger.warn("Actions for entity are being overwritten", { entity });
    }
    this.registry.set(entity, actions as DetailActionStrict<any>[]);
  }

  /**
   * Check if an entity has registered actions
   */
  hasActionsForEntity(entity: EntityKey): boolean {
    return this.registry.has(entity);
  }

  /**
   * Get all registered entities
   */
  getRegisteredEntities(): EntityKey[] {
    return Array.from(this.registry.keys());
  }

  /**
   * Clear all registered actions (useful for testing)
   */
  clear(): void {
    this.registry.clear();
  }
}

/**
 * Singleton instance of the action registry
 */
export const actionRegistry = new ActionRegistry();

/**
 * Initialize the registry with entity action maps
 * Called once at application startup
 */
export function initializeActionRegistry(
  entityActionMap: Partial<EntityActionMapType>
): void {
  for (const [entity, actionsFn] of Object.entries(entityActionMap)) {
    if (typeof actionsFn === "function") {
      actionRegistry.registerActionsForEntity(
        entity as EntityKey,
        actionsFn() as DetailActionStrict<any>[]
      );
    }
  }
}

export type { IActionRegistry };
